import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Tooltip, IconButton } from '@mui/material';
import { Add as AddIcon, FilterList as FilterListIcon, PlayArrow as ExecuteIcon, PlayCircleOutline as StartIcon } from '@mui/icons-material';
import { PermissionButton } from '@/components/common/PermissionButton';
import { TanStackTableViewWithSchema } from '@/components/common/Table';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import { hasActiveFilters } from '@/utils/helpers';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import activityService from '../../services/activityService';
import { ActivityStatus } from '../../enums/schedule.enums';

const ActivityListPage: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [totalCount, setTotalCount] = React.useState<number>(0);

  const {
    page,
    rowsPerPage,
    searchTerm,
    sortKey,
    sortDirection,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    handleFilterChange,
    clearFilters,
    toDetail,
  } = useCompletePaginationNavigation(navigate, '/schedule-management/activities');

  const queryParams = useMemo(() => {
    const apiParams: Record<string, unknown> = {
      page: page + 1,
      limit: rowsPerPage,
      cf: 'true',
    };
    if (searchTerm) apiParams.search = searchTerm;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        apiParams[key] = value;
      }
    });
    if (sortKey) {
      apiParams.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
    }
    return apiParams;
  }, [page, rowsPerPage, searchTerm, filters, sortKey, sortDirection]);

  const fetchActivities = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await activityService.getActivities(queryParams);
      if (result?.activities) {
        setActivities(result.activities);
        setTotalCount(result.totalCount || 0);
      } else if (result?.results) {
        setActivities(result.results);
        setTotalCount(result.count || 0);
      } else if (Array.isArray(result)) {
        setActivities(result);
        setTotalCount(result.length);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Handle execution navigation (only for IN_PROGRESS / PAUSED activities)
  const handleExecuteActivity = (id: string | number) => {
    navigate(`/schedule-management/activities/${id}/execute`);
  };

  // Handle start — assigns activity to current user and transfers asset ownership
  const handleStartActivity = React.useCallback(async (id: string | number) => {
    try {
      await activityService.startActivity(String(id));
      fetchActivities();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to start activity. Please try again.';
      setError(message);
    }
  }, [fetchActivities]);

  // Render custom action buttons for each row
  const renderRowActions = React.useCallback((row: any) => (
    <>
      {row.status === ActivityStatus.PENDING && (
        <Tooltip title="Start Activity">
          <PermissionButton
            module="activity"
            permissionAction="execute"
            component={IconButton}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleStartActivity(row.id);
            }}
            sx={{
              color: '#1976d2',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
            }}
          >
            <StartIcon />
          </PermissionButton>
        </Tooltip>
      )}
      {(row.status === ActivityStatus.IN_PROGRESS || row.status === ActivityStatus.PAUSED) && (
        <Tooltip title="Execute Activity">
          <PermissionButton
            module="activity"
            permissionAction="execute"
            component={IconButton}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleExecuteActivity(row.id);
            }}
            sx={{
              color: '#2e7d32',
              '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' },
            }}
          >
            <ExecuteIcon />
          </PermissionButton>
        </Tooltip>
      )}
    </>
  ), [navigate, handleStartActivity]);

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">Activities</h3>
      <div className="appGridContainer">
        <div className="gridHeader">
          <div className="gridHeadLeft">
            <div className="inlineFormContainer">
              <Button
                variant="outlined"
                color="primary"
                onClick={clearFilters}
                size="small"
                sx={{ ml: 2, height: 40 }}
                startIcon={<FilterListIcon />}
                disabled={!hasActiveFilters(filters)}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          <div className="gridHeadRight">
            <PermissionButton
              module="activity"
              permissionAction="add"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/schedule-management/activities/create-one-off')}
            >
              Add One-Off Activity
            </PermissionButton>
          </div>
        </div>

        <div className="gridWrapper">
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TanStackTableViewWithSchema
              data={activities}
              rowKey={(row: any) => row.id}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              handleView={(id) => toDetail(String(id))}
              loading={loading}
              permissionModule="activity"
              entity="activity"
              enableAPIConfig={true}
              onFilterChange={handleFilterChange}
              filterValues={filters}
              fallbackHeaders={[]}
              renderRowActions={renderRowActions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityListPage;
